import type { BulkJob } from "../Types/accountQuery";
import type { CampaignGuidanceUpdate } from "../Api/campaignGuidanceUpdates";
import type { SmartleadSubmission } from "../Api/smartlead";
import type { SeriesRevision } from "../Api/socialPromptRevisions";
import type { ClientDeletionJob } from "../Api/deleteClient";
import type { AccountDeletionJob } from "../Api/deleteAccount";
import type { AssistantGenerationResult } from "../Types/assistant";
import type { Run } from "../features/insights/contracts.generated";
import type { Run as AssetRun } from "../features/asset-monitoring/contracts.generated";
import { accountEditPending } from "../Components/Account/accountEditStatus";
import { getUserFromLS } from "../utils/getUserFromLS";
import type { JobReadResult, JobReference } from "./JobRefreshCoordinator";

export interface JobTypes {
  campaign_guidance_update: CampaignGuidanceUpdate;
  account_bulk: BulkJob;
  insight_run: Run;
  asset_run: AssetRun;
  smartlead_submission: SmartleadSubmission;
  social_revision: SeriesRevision;
  client_deletion: ClientDeletionJob;
  account_deletion: AccountDeletionJob;
  assistant_generation: AssistantGenerationResult;
}
export interface JobScope { clientId: string; actorId: string }
type Registration<T> = {
  owner: JobReference["owner"];
  path: (id: string, scope: JobScope) => string;
  unfinished: (value: T) => boolean;
  resources: readonly string[];
  destination: (id: string, scope: JobScope) => string;
};

/** Only registered owners, API paths, and destinations are used for delivery. */
export const jobRegistry: { [K in keyof JobTypes]: Registration<JobTypes[K]> } = {
  campaign_guidance_update: {
    owner: "database_service",
    path: id => `/campaign-guidance-updates/${encodeURIComponent(id)}`,
    unfinished: value => ["queued", "running"].includes(value.status),
    resources: ["campaign_guidance"],
    destination: (id, scope) => `/settings?${new URLSearchParams({ section: "campaigns", client: scope.clientId, guidanceUpdate: id })}`,
  },
  assistant_generation: {
    owner: "database_service",
    path: id => `/assistant/proposals/${encodeURIComponent(id)}/job-status`,
    unfinished: value => ["queued", "in_progress", "retrying", "unknown"].includes(value.state),
    resources: ["assistant_proposal"],
    destination: (id, scope) => `/jobs/assistant_generation/${encodeURIComponent(id)}?${new URLSearchParams({ clientId: scope.clientId })}`,
  },
  account_deletion: {
    owner: "database_service",
    path: id => `/account-deletion-jobs/${encodeURIComponent(id)}`,
    unfinished: value => ["queued", "running"].includes(value.status),
    resources: ["accounts"],
    destination: (id, scope) => `/jobs/account_deletion/${encodeURIComponent(id)}?${new URLSearchParams({ clientId: scope.clientId })}`,
  },
  social_revision: {
    owner: "database_service",
    path: id => `/social_prompts/revisions/${encodeURIComponent(id)}`,
    unfinished: value => value.status === "queued",
    resources: ["social_series"],
    destination: (id, scope) => `/jobs/social_revision/${encodeURIComponent(id)}?${new URLSearchParams({ clientId: scope.clientId })}`,
  },
  client_deletion: {
    owner: "database_service",
    path: id => `/client-deletion-jobs/${encodeURIComponent(id)}`,
    unfinished: value => ["queued", "running"].includes(value.status),
    resources: ["clients"],
    destination: (id, scope) => `/jobs/client_deletion/${encodeURIComponent(id)}?${new URLSearchParams({ clientId: scope.clientId })}`,
  },
  smartlead_submission: {
    owner: "account_orchestrator",
    path: (id, scope) => `/jobs/smartlead-submissions/${encodeURIComponent(id)}?client_id=${encodeURIComponent(scope.clientId)}`,
    unfinished: value => (!value.task_completed_at && !!value.task_completion_requested) || ["pending", "dispatching", "queued", "uncertain"].includes(value.status),
    resources: ["smartlead_queue", "account"],
    destination: (_id, scope) => `/sales-home?${new URLSearchParams({ clientId: scope.clientId })}`,
  },
  asset_run: {
    owner: "database_service",
    path: (id, scope) => `/clients/${encodeURIComponent(scope.clientId)}/asset-monitoring/runs/${encodeURIComponent(id)}`,
    unfinished: value => ["queued", "running", "waiting"].includes(value.state),
    resources: ["asset_publications", "asset_source", "asset_preview", "asset"],
    destination: (id, scope) => `/my-assets?${new URLSearchParams({ clientId: scope.clientId, view: "sources", runId: id })}`,
  },
  account_bulk: {
    owner: "database_service",
    path: id => `/account-bulk-jobs/${encodeURIComponent(id)}`,
    unfinished: accountEditPending,
    resources: ["accounts"],
    destination: (id, scope) => `/all-accounts?${new URLSearchParams({ clientId: scope.clientId, jobId: id })}`,
  },
  insight_run: {
    owner: "database_service",
    path: (id, scope) => `/clients/${encodeURIComponent(scope.clientId)}/insights/runs/${encodeURIComponent(id)}`,
    unfinished: value => ["queued", "running"].includes(value.status),
    resources: ["insights", "draft"],
    destination: (id, scope) => `/insights/sources?${new URLSearchParams({ clientId: scope.clientId, runId: id })}`,
  },
};

export async function readRegisteredJob<K extends keyof JobTypes>(
  kind: K, job: JobReference, scope: JobScope, signal: AbortSignal,
): Promise<JobReadResult & { value?: JobTypes[K] }> {
  const registration = jobRegistry[kind];
  const user = getUserFromLS();
  if (job.owner !== registration.owner || job.kind !== kind || user?.id !== scope.actorId)
    return { version: 0, unfinished: false, accessDenied: true };
  const baseUrl = job.owner === "account_orchestrator" ? import.meta.env.VITE_ORCHESTRATOR_BASE_URL : import.meta.env.VITE_API_BASE_URL;
  const response = await fetch(`${baseUrl}${registration.path(job.id, scope)}`, {
    signal, headers: { Authorization: `Bearer ${user.access_token}` }, cache: "no-store",
  });
  if ([401, 403, 404].includes(response.status))
    return { version: 0, unfinished: false, accessDenied: true };
  if (!response.ok) throw new Error("Could not refresh this job. Please retry.");
  const value = await response.json() as JobTypes[K];
  if (kind === "campaign_guidance_update" && (!('client_id' in value) || value.client_id !== scope.clientId))
    throw new Error("The job response did not match the requested organization.");
  const reference = value.job_reference;
  const version = value.change_version ?? 0;
  const logicalRun = kind === "insight_run" && "recovery" in value
    && value.recovery?.request_id === reference?.id;
  if (!Number.isSafeInteger(version) || version < 0 ||
      (reference && (reference.owner !== job.owner || reference.kind !== kind || (reference.id !== job.id && !logicalRun))))
    throw new Error("The job response did not match the requested job.");
  if (getUserFromLS()?.id !== scope.actorId || signal.aborted)
    throw new DOMException("Session changed", "AbortError");
  return {
    value, version, job: reference ?? job,
    unfinished: registration.unfinished(value),
    requester: value.requester_id === scope.actorId,
  };
}
